/**
 * Rolling Stats ECharts Adapter
 *
 * Transforms rolling statistics data into ECharts line chart options.
 * Supports various statistics: volatility, returns, sharpe, max_drawdown, beta, correlation, underwater.
 */

import type { EChartsCoreOption } from "echarts";
import * as echarts from "echarts";
import type {
  RollingStatsAdapterProps,
  EChartAdapterResult,
  DataPoint,
  ExtendedAssetInfo,
} from "./types";
import { createCategoryAxisLabelInterval } from "./axisDateLabels";
import {
  buildProxyAwareLineSeries,
  findProxyExtensionForAsset,
  getProxyExtensionsFromStats,
} from "./proxySeriesUtils";

/**
 * Map statistic names to display labels
 */
const STATISTIC_LABELS: Record<string, string> = {
  volatility: "Volatility",
  returns: "Returns",
  sharpe_ratio: "Sharpe Ratio",
  sharpe: "Sharpe Ratio",
  sortino_ratio: "Sortino Ratio",
  sortino: "Sortino Ratio",
  max_drawdown: "Max Drawdown",
  beta: "Beta",
  correlation: "Correlation",
  underwater: "Drawdown",
  alpha: "Alpha",
};

/**
 * Statistics that should be shown as percentages
 */
const PERCENTAGE_STATISTICS = new Set([
  "volatility",
  "returns",
  "max_drawdown",
  "underwater",
]);

/**
 * Format statistic name for display
 */
function formatStatisticName(statistic: string): string {
  return (
    STATISTIC_LABELS[statistic] ||
    statistic
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

/**
 * Check if statistic should be displayed as percentage
 */
function isPercentageStatistic(statistic: string): boolean {
  return PERCENTAGE_STATISTICS.has(statistic);
}

/**
 * Format value based on statistic type
 */
function formatValue(value: number, statistic: string): string {
  if (isPercentageStatistic(statistic)) {
    const percentValue = value * 100;
    const rounded = Math.round(percentValue * 10) / 10;
    const formatted = Math.abs(rounded - Math.round(rounded)) < 0.001 
      ? Math.round(rounded).toString() 
      : rounded.toFixed(1);
    return `${formatted}%`;
  }
  const rounded = Math.round(value * 10) / 10;
  const formatted = Math.abs(rounded - Math.round(rounded)) < 0.001 
    ? Math.round(rounded).toString() 
    : rounded.toFixed(1);
  return formatted;
}

/**
 * Get ordinal suffix for a day number (1st, 2nd, 3rd, etc.)
 */
function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

/**
 * Format date for axis labels (short format: "Jan 2024")
 */
function formatDateForAxis(date: Date): string {
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${month} ${year}`;
}

/**
 * Format date for tooltips (full format: "15th Jan 2026")
 */
function formatDateForTooltip(date: Date): string {
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
}

/**
 * Convert hex color to rgba string
 */
function hexToRgba(hex: string, alpha: number): string {
  // Remove # if present
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Create area style gradient for regular charts (top-to-bottom fade)
 */
function createAreaGradient(
  color: string,
  opacity: number = 0.3
): echarts.graphic.LinearGradient {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: hexToRgba(color, opacity) },
    { offset: 1, color: hexToRgba(color, 0) },
  ]);
}

/**
 * Create area style gradient for underwater charts using ECharts graphic.LinearGradient
 */
function createUnderwaterGradient(
  color: string
): echarts.graphic.LinearGradient {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: hexToRgba(color, 0) }, // Transparent at top
    { offset: 1, color: hexToRgba(color, 0.4) }, // Semi-transparent at bottom
  ]);
}

/**
 * Create a diagonal line pattern for chart background
 * Returns a pattern object that can be used as backgroundColor in ECharts
 */
function createPatternBackground(isDarkMode: boolean): object {
  // Create a canvas pattern for diagonal lines
  const patternSize = 10;
  const canvas = document.createElement("canvas");
  canvas.width = patternSize;
  canvas.height = patternSize;
  const ctx = canvas.getContext("2d");
  
  if (ctx) {
    // Set transparent background
    ctx.clearRect(0, 0, patternSize, patternSize);
    
    // Draw diagonal line
    ctx.strokeStyle = isDarkMode ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, patternSize);
    ctx.lineTo(patternSize, 0);
    ctx.stroke();
  }
  
  return {
    image: canvas,
    repeat: "repeat",
  };
}

/**
 * Calculate a "nice" number (round number) for axis intervals
 * Based on the "nice numbers" algorithm for pretty axis labels
 */
function calculateNiceNumber(range: number, round: boolean): number {
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * Math.pow(10, exponent);
}

/**
 * Calculate smart Y-axis domain with clean intervals
 */
function calculateYDomain(seriesData: any[], isUnderwater: boolean, desiredTicks: number = 5): { 
  min: number; 
  max: number; 
  interval: number;
} {
  const allValues: number[] = [];
  seriesData.forEach((s) => {
    if (Array.isArray(s.data)) {
      s.data.forEach((v: any) => {
        if (typeof v === "number" && !isNaN(v)) {
          allValues.push(v);
        }
      });
    }
  });

  if (allValues.length === 0) return { min: 0, max: 100, interval: 20 };

  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);
  const dataRange = dataMax - dataMin;

  // Add padding to the data range
  const paddingPercent = 0.1;
  const paddedRange = dataRange * (1 + paddingPercent * 2);
  
  // Calculate a nice interval
  const rawInterval = paddedRange / desiredTicks;
  const interval = calculateNiceNumber(rawInterval, true);

  // Round min down and max up to nearest interval
  const niceMin = Math.floor(dataMin / interval) * interval;
  let niceMax = Math.ceil(dataMax / interval) * interval;

  // For underwater charts, ensure we always include 0
  if (isUnderwater) {
    niceMax = Math.max(0, niceMax);
  }

  return { min: niceMin, max: niceMax, interval };
}

/**
 * Build ECharts option for rolling stats charts
 */
export function buildRollingStatsOption(
  props: RollingStatsAdapterProps
): EChartAdapterResult {
  const {
    rawData,
    selectedAssets,
    assetDisplayNames,
    assetColors,
    colorPalette,
    chartSettings,
    visualSettings,
    isDarkMode,
    assetStats,
    assetMetadata,
  } = props;

  const statistic = chartSettings.statistic || "volatility";
  const isUnderwater = statistic === "underwater";
  const smoothLines = visualSettings.smoothLines ?? false;
  const showAreaGradient = visualSettings.showAreaGradient ?? true;
  const showGridRows = visualSettings.showGridRows ?? true;
  const showGridColumns = visualSettings.showGridColumns ?? false;
  const showCrosshair = visualSettings.showCrosshair ?? true;
  const showTooltips = visualSettings.showTooltips ?? true;
  const showXAxis = visualSettings.showXAxis ?? true;
  const showYAxis = visualSettings.showYAxis ?? true;
  const showPatternBackground = visualSettings.showPatternBackground ?? false;

  // Handle empty data
  if (!rawData || rawData.length === 0 || selectedAssets.length === 0) {
    return {
      option: {},
      chartType: "line",
      hasData: false,
    };
  }

  // Extract raw dates for tooltip and formatted dates for x-axis
  const rawDates = rawData.map((point: DataPoint) => point.date);
  const axisLabelInterval = createCategoryAxisLabelInterval(rawDates);
  const dates = rawData.map((point: DataPoint) => {
    const date = point.date;
    return date instanceof Date ? formatDateForAxis(date) : String(date);
  });

  // Theme colors
  const textColor = isDarkMode ? "#e2e8f0" : "#334155";
  const gridColor = isDarkMode ? "#475569" : "#e2e8f0";
  const axisLineColor = isDarkMode ? "#64748b" : "#94a3b8";

  const proxyExtensions = getProxyExtensionsFromStats(assetStats);
  const metadataByAsset = assetMetadata as Record<string, ExtendedAssetInfo>;

  // Build series for each asset
  const series = selectedAssets.flatMap((assetId: string, index: number) => {
    const color = assetColors[assetId] || colorPalette[index % colorPalette.length];
    const displayName = assetDisplayNames[assetId] || assetId;
    const extension = findProxyExtensionForAsset(
      assetId,
      metadataByAsset,
      proxyExtensions
    );

    const proxySeries = buildProxyAwareLineSeries({
      assetId,
      displayName,
      color,
      rawData,
      extension,
      smoothLines,
      showAreaGradient,
      createAreaGradient,
    });

    return proxySeries.map((seriesConfig) => {
      const nextSeries: any = {
        ...seriesConfig,
        lineStyle: {
          ...seriesConfig.lineStyle,
          width: 2,
        },
        itemStyle: {
          color,
        },
        emphasis: {
          focus: "series",
          lineStyle: {
            width: 3,
          },
        },
        _resourceId: assetId,
      };

      if (showAreaGradient && nextSeries.areaStyle) {
        if (isUnderwater) {
          nextSeries.areaStyle = {
            color: createUnderwaterGradient(color),
            origin: "auto",
          };
        }
      }

      return nextSeries;
    });
  });

  // Determine if we need to show zero line (for underwater charts)
  const needsZeroLine = isUnderwater;

  // Calculate smart Y-axis domain with clean intervals
  const yDomain = calculateYDomain(series, isUnderwater, 5);

  // Build the ECharts option
  const option: EChartsCoreOption = {
    backgroundColor: showPatternBackground ? createPatternBackground(isDarkMode) : "transparent",
    tooltip: showTooltips
      ? {
          trigger: "axis",
          axisPointer: {
            type: showCrosshair ? "cross" : "line",
            crossStyle: {
              color: axisLineColor,
            },
          },
          backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: {
            color: textColor,
          },
          formatter: (params: any) => {
            if (!Array.isArray(params) || params.length === 0) return "";

            // Get the raw date using dataIndex and format it for tooltip
            const dataIndex = params[0].dataIndex;
            const rawDate = rawDates[dataIndex];
            const dateLabel = rawDate instanceof Date 
              ? formatDateForTooltip(rawDate) 
              : params[0].axisValue;
            let content = `<div style="font-weight: 600; margin-bottom: 8px;">${dateLabel}</div>`;

            params.forEach((param: any) => {
              if (param.value !== null && param.value !== undefined) {
                const formattedValue = formatValue(param.value, statistic);
                content += `
                  <div style="display: flex; align-items: center; gap: 8px; margin: 4px 0;">
                    <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${param.color};"></span>
                    <span style="flex: 1;">${param.seriesName}:</span>
                    <span style="font-weight: 600;">${formattedValue}</span>
                  </div>
                `;
              }
            });

            return content;
          },
        }
      : undefined,
    legend: {
      show: false, // Use custom legend
    },
    grid: {
      left: showYAxis ? 80 : 10,
      right: 20,
      top: 20,
      bottom: showXAxis ? 60 : 30,
      containLabel: false,
      borderWidth: 0,
    },
    xAxis: {
      type: "category",
      data: dates,
      boundaryGap: false,
      name: showXAxis ? "Date" : "",
      nameLocation: "center",
      nameGap: 40,
      nameTextStyle: {
        color: textColor,
        fontSize: 12,
        fontFamily: "monospace",
      },
      show: true, // Always show for grid lines, but control individual elements
      axisLine: {
        show: showXAxis,
        onZero: needsZeroLine,
        lineStyle: {
          color: axisLineColor,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: showXAxis, // Toggle labels based on showXAxis setting
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        interval: axisLabelInterval,
        margin: 10,
      },
      splitLine: {
        show: showGridColumns,
        lineStyle: {
          color: gridColor,
          type: "solid",
          opacity: 0.3,
        },
      },
    },
    yAxis: {
      type: "value",
      name: showYAxis ? formatStatisticName(statistic) : "",
      nameLocation: "middle",
      nameGap: 50,
      nameTextStyle: {
        color: textColor,
        fontSize: 12,
        fontFamily: "monospace",
      },
      min: yDomain.min,
      max: yDomain.max,
      interval: yDomain.interval,
      show: true, // Always show for grid lines, but control individual elements
      axisLine: {
        show: showYAxis,
        lineStyle: {
          color: axisLineColor,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: showYAxis, // Toggle labels based on showYAxis setting
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        formatter: (value: number) => formatValue(value, statistic),
        margin: 15,
      },
      splitLine: {
        show: showGridRows,
        lineStyle: {
          color: gridColor,
          type: "solid",
          opacity: 0.3,
        },
      },
    },
    series,
    color: colorPalette,
  };

  return {
    option,
    chartType: isUnderwater ? "area" : "line",
    hasData: true,
  };
}
